/**
 * TEMPORARY — stage-2 glitch-catalog instrument (2026-08-12).
 *
 * Records every overwrite of a displayed trade-path value: which surface and
 * field, what value, who wrote it, and on what trigger. It exists to turn
 * "the number flickered" into an ordered list of writes with attribution,
 * the same way `position_steps` turned PnL flicker into data.
 *
 * OFF by default. Enabled only when one of:
 *   - globalThis.__WT_DISPLAY_DIAG === true   (the scenario stand / tests)
 *   - localStorage 'wt-display-diag' === '1'  (a manual browser session)
 *
 * It is display-only and fire-and-forget: it never throws into the money
 * path, never influences a decision, and is due for removal (or permanent
 * flag-gating) when the stage-2 catalog closes.
 */

export type DisplayWrite = {
  /** ms since arm (or since module load when never armed). */
  at: number
  /** 'rail' | 'instant' | 'balance' | 'portfolio' | 'toast' | 'exec' */
  surface: string
  /** e.g. 'PnL', 'Holding', 'gram-balance', 'carry', 'freeze', 'phase' */
  field: string
  value: string
  /** Who wrote it: 'canon' | 'carry' | 'freeze' | 'getter' | 'ws-push' | ... */
  writer: string
  /** What caused it: 'click' | 'price-tick' | 'portfolio-answer' | ... */
  trigger: string
}

const MAX_ENTRIES = 2_000

let entries: DisplayWrite[] = []
let epoch = Date.now()
// Last value per surface:field — consecutive identical writes carry no
// information (a steady value is not an overwrite) and are dropped.
let last = new Map<string, string>()

function flagEnabled(): boolean {
  try {
    if ((globalThis as { __WT_DISPLAY_DIAG?: boolean }).__WT_DISPLAY_DIAG === true) return true
  } catch {
    // no global — fall through
  }
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('wt-display-diag') === '1'
  } catch {
    return false
  }
}

export function displayDiagEnabled(): boolean {
  return flagEnabled()
}

/** Clears the log and restarts the clock. The stand calls this per scenario. */
export function armDisplayDiag(): void {
  entries = []
  last = new Map()
  epoch = Date.now()
}

export function noteDisplayWrite(
  surface: string,
  field: string,
  value: string,
  writer: string,
  trigger: string
): void {
  try {
    if (!flagEnabled() || entries.length >= MAX_ENTRIES) return
    const key = `${surface}:${field}`
    if (last.get(key) === value) return
    last.set(key, value)
    entries.push({ at: Date.now() - epoch, surface, field, value, writer, trigger })
  } catch {
    // A diagnostic must never throw into the path it observes.
  }
}

/**
 * A transition event (freeze on/off, carry set/drop, a push held/released).
 * Unlike noteDisplayWrite it is NOT deduplicated: two identical transitions
 * at different times are two facts.
 */
export function noteDisplayEvent(
  surface: string,
  field: string,
  value: string,
  writer: string,
  trigger: string
): void {
  try {
    if (!flagEnabled() || entries.length >= MAX_ENTRIES) return
    last.set(`${surface}:${field}`, value)
    entries.push({ at: Date.now() - epoch, surface, field, value, writer, trigger })
  } catch {
    // ignored
  }
}

export function readDisplayDiag(): readonly DisplayWrite[] {
  return entries
}

export function formatDisplayDiag(): string {
  return entries
    .map(
      (e) =>
        `${String(e.at).padStart(6, ' ')}ms  ${e.surface.padEnd(9)} ${e.field.padEnd(16)} ` +
        `= ${e.value}  [${e.writer} / ${e.trigger}]`
    )
    .join('\n')
}
